import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Badge,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  VisibilityOff as HiddenIcon,
  DragIndicator as DragIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { EntityType } from '../types/prompts';

interface Props {
  entityTypes: EntityType[];
  selectedSlug: string;
  onSelect: (slug: string) => void;
  onCreateNew: () => void;
  onReorder: (slugs: string[]) => void;
}

function SortableItem({ et, isSelected, onSelect }: {
  et: EntityType;
  isSelected: boolean;
  onSelect: (slug: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: et.slug });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : et.is_visible ? 1 : 0.45,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <ListItemButton
      ref={setNodeRef}
      style={style}
      selected={isSelected}
      onClick={() => onSelect(et.slug)}
      sx={{
        py: 0.75,
        px: 0.5,
        '&.Mui-selected': {
          backgroundColor: 'action.selected',
        },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: 'text.disabled',
          mr: 0.5,
          '&:active': { cursor: 'grabbing' },
        }}
        onClick={e => e.stopPropagation()}
      >
        <DragIcon sx={{ fontSize: 16 }} />
      </Box>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              variant="body2"
              noWrap
              sx={{ fontWeight: isSelected ? 600 : 400, maxWidth: 170 }}
            >
              {et.label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {et.entity_count > 0 && (
                <Badge
                  badgeContent={et.entity_count}
                  color="default"
                  sx={{
                    '& .MuiBadge-badge': {
                      position: 'static',
                      transform: 'none',
                      fontSize: '0.7rem',
                      minWidth: 18,
                      height: 18,
                      borderRadius: 9,
                      backgroundColor: 'action.hover',
                    },
                  }}
                />
              )}
              {!et.is_visible && (
                <HiddenIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              )}
            </Box>
          </Box>
        }
      />
    </ListItemButton>
  );
}

export default function EntityTypeSidebar({ entityTypes, selectedSlug, onSelect, onCreateNew, onReorder }: Props) {
  const [search, setSearch] = useState('');
  const [visibleOnly, setVisibleOnly] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const isFiltering = search.length > 0;

  const filtered = entityTypes.filter(et => {
    if (visibleOnly && !et.is_visible) return false;
    if (search) {
      const q = search.toLowerCase();
      return et.label.toLowerCase().includes(q) || et.slug.toLowerCase().includes(q);
    }
    return true;
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    if (visibleOnly) {
      // Reorder only visible items, preserving hidden items' positions
      const visibleSlugs = filtered.map(et => et.slug);
      const oldIdx = visibleSlugs.indexOf(active.id as string);
      const newIdx = visibleSlugs.indexOf(over.id as string);
      const reorderedVisible = arrayMove(visibleSlugs, oldIdx, newIdx);

      // Merge back: walk full list, replace visible items in new order
      let vi = 0;
      const merged = entityTypes.map(et => {
        if (et.is_visible) return reorderedVisible[vi++];
        return et.slug;
      });
      onReorder(merged);
    } else {
      const oldIndex = entityTypes.findIndex(et => et.slug === active.id);
      const newIndex = entityTypes.findIndex(et => et.slug === over.id);
      const reordered = arrayMove(entityTypes, oldIndex, newIndex);
      onReorder(reordered.map(et => et.slug));
    }
  };

  const listContent = (
    <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
      {filtered.map(et => (
        <SortableItem
          key={et.slug}
          et={et}
          isSelected={et.slug === selectedSlug}
          onSelect={onSelect}
        />
      ))}
      {filtered.length === 0 && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No matching types
          </Typography>
        </Box>
      )}
    </List>
  );

  // Items for sortable context — use filtered list when visibility filter is on
  const sortableItems = filtered.map(et => et.slug);

  return (
    <Box sx={{
      width: 280,
      minWidth: 280,
      borderRight: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}>
      {/* Search + visibility filter */}
      <Box sx={{ p: 1.5, pb: 1, display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Filter types..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
        />
        <Tooltip title={visibleOnly ? 'Showing visible only' : 'Showing all'}>
          <IconButton
            size="small"
            onClick={() => setVisibleOnly(v => !v)}
            sx={{
              color: visibleOnly ? 'primary.main' : 'text.disabled',
            }}
          >
            <VisibilityIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* List - only sortable when not text-filtering */}
      {isFiltering ? (
        listContent
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
            {listContent}
          </SortableContext>
        </DndContext>
      )}

      {/* Create button */}
      <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={onCreateNew}
        >
          New Entity Type
        </Button>
      </Box>
    </Box>
  );
}
